package fr.frux.feature.notifications

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.shared.domain.model.Notification
import fr.frux.shared.domain.model.NotificationKind
import fr.frux.shared.domain.model.NotificationSubjectType
import fr.frux.shared.domain.model.hasDestination
import fr.frux.shared.domain.notification.NotificationBadgeStore
import fr.frux.shared.domain.notification.NotificationTitles
import fr.frux.shared.domain.repository.NotificationRepository
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** Une notification prête pour l'affichage (#225 P0). */
data class NotificationItem(
    val id: Long,
    val kind: NotificationKind,
    val subjectType: NotificationSubjectType,
    val subjectId: Long,
    val title: String,
    val isUnread: Boolean,
    val createdAt: Long,
    /** Calculée sur la notification entière (#550) : un arbre masqué par la modération n'ouvre rien. */
    val hasDestination: Boolean = subjectType.hasDestination,
)

data class NotificationsState(
    val items: List<NotificationItem> = emptyList(),
    val isLoading: Boolean = false,
    val isLoadingMore: Boolean = false,
    /** Catégorie d'échec (#176) ; le libellé est résolu par l'écran. */
    val error: AppError? = null,
    val hasMore: Boolean = false,
)

/**
 * Écran Activité (#225 P0) : liste paginée des notifications, non-lues d'abord (contrat serveur).
 *
 * Le compteur du badge de la barre du bas vit dans [NotificationBadgeStore] — un VM permanent
 * (#43, piège Nav3) —, pas ici : cet écran le rafraîchit à l'ouverture et après une lecture, mais
 * ne le porte pas lui-même.
 */
class NotificationsViewModel(
    private val notificationRepository: NotificationRepository,
    private val badgeStore: NotificationBadgeStore,
) : ViewModel() {
    private val _state = MutableStateFlow(NotificationsState())
    val state: StateFlow<NotificationsState> = _state.asStateFlow()

    private var nextCursor: Long? = null
    private var loadJob: Job? = null

    init {
        load()
    }

    /** (Re)charge la première page — appelé à l'ouverture de l'écran (cf. `NotificationsScreen`). */
    fun load() {
        loadJob?.cancel()
        loadJob =
            viewModelScope.launch {
                _state.update { it.copy(isLoading = true, error = null) }
                notificationRepository.getNotifications(beforeId = null).fold(
                    onSuccess = { page ->
                        nextCursor = page.nextCursor
                        _state.update {
                            it.copy(
                                items = page.notifications.map { n -> n.toItem() },
                                isLoading = false,
                                error = null,
                                hasMore = page.nextCursor != null,
                            )
                        }
                    },
                    onFailure = { error ->
                        _state.update { it.copy(isLoading = false, error = error.asAppError()) }
                    },
                )
                badgeStore.refresh()
            }
    }

    /** Pagination « charger plus » simple en fin de liste (#225 P0, pas de scroll infini). */
    fun loadMore() {
        val cursor = nextCursor ?: return
        if (_state.value.isLoadingMore) return
        viewModelScope.launch {
            _state.update { it.copy(isLoadingMore = true) }
            notificationRepository.getNotifications(beforeId = cursor).fold(
                onSuccess = { page ->
                    nextCursor = page.nextCursor
                    _state.update {
                        it.copy(
                            items = it.items + page.notifications.map { n -> n.toItem() },
                            isLoadingMore = false,
                            hasMore = page.nextCursor != null,
                        )
                    }
                },
                onFailure = { error ->
                    _state.update { it.copy(isLoadingMore = false, error = error.asAppError()) }
                },
            )
        }
    }

    /**
     * Marque [id] comme lu (clic sur un item avant navigation vers le sujet, cf.
     * `NotificationsScreen`) — optimiste côté liste, la vérité serveur revient au prochain [load].
     */
    fun markRead(id: Long) {
        if (_state.value.items.none { it.id == id && it.isUnread }) return
        _state.update { s -> s.copy(items = s.items.map { if (it.id == id) it.copy(isUnread = false) else it }) }
        viewModelScope.launch {
            notificationRepository.markRead(listOf(id))
            badgeStore.refresh()
        }
    }

    /** Action « Tout marquer comme lu » (#225 P0). */
    fun markAllRead() {
        _state.update { s -> s.copy(items = s.items.map { it.copy(isUnread = false) }) }
        viewModelScope.launch {
            notificationRepository.markAllRead()
            badgeStore.refresh()
        }
    }

    companion object {
        private fun Notification.toItem(): NotificationItem =
            NotificationItem(
                id = id,
                kind = kind,
                subjectType = subjectType,
                subjectId = subjectId,
                title = NotificationTitles.of(kind, payload, subjectType),
                isUnread = isUnread,
                createdAt = createdAt,
                hasDestination = hasDestination,
            )
    }
}
